import React from 'react';
import {
  Check,
  ChevronRight,
  ChevronDown,
  Database,
  Folder,
  FolderOpen,
  Server,
  Box,
  Table,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { isLocalDatasetType } from '../../utils/datasetDisplayUtils';

export const MANTEO_CLOUD_ID = 'manteo';
export const MANTEO_USER_DATA_KEY = 'user-data';
export const MANTEO_DATA_CATALOG_KEY = 'data-catalog';

const MANTEO_SUBGROUP_ORDER = [MANTEO_USER_DATA_KEY, MANTEO_DATA_CATALOG_KEY];

export const manteoSubgroupNodeId = (subKey) => `${MANTEO_CLOUD_ID}.${subKey}`;

export const emptyManteoSubgroups = () => ({
  [MANTEO_USER_DATA_KEY]: [],
  [MANTEO_DATA_CATALOG_KEY]: [],
});

export const sortByCreatedAtDesc = (items) =>
  [...items].sort((a, b) => {
    const aTs = a.created_at ? new Date(a.created_at).getTime() : 0;
    const bTs = b.created_at ? new Date(b.created_at).getTime() : 0;
    return bTs - aTs;
  });

export const sortBigQueryTreeByCreatedAt = (bigqueryTree) => {
  const sorted = {};
  Object.entries(bigqueryTree).forEach(([project, datasets]) => {
    sorted[project] = {};
    Object.entries(datasets).forEach(([dataset, items]) => {
      sorted[project][dataset] = sortByCreatedAtDesc(items);
    });
  });
  return sorted;
};

export const formatItemDate = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const splitManteoDatasets = (items) => {
  const subgroups = emptyManteoSubgroups();
  items.forEach((d) => {
    if (d.type === 'sample') {
      subgroups[MANTEO_DATA_CATALOG_KEY].push(d);
    } else {
      subgroups[MANTEO_USER_DATA_KEY].push(d);
    }
  });
  subgroups[MANTEO_USER_DATA_KEY] = sortByCreatedAtDesc(subgroups[MANTEO_USER_DATA_KEY]);
  subgroups[MANTEO_DATA_CATALOG_KEY] = sortByCreatedAtDesc(subgroups[MANTEO_DATA_CATALOG_KEY]);
  return subgroups;
};

/** @returns {{ manteo: Record<string, object[]>, bigquery: Record<string, Record<string, object[]>> }} */
export const buildSortedDatasetTree = (datasets) => {
  const tree = {
    manteo: emptyManteoSubgroups(),
    bigquery: {},
  };

  const localItems = [];

  (Array.isArray(datasets) ? datasets : []).forEach((d) => {
    if (isLocalDatasetType(d.type)) {
      localItems.push(d);
    } else if (d.type === 'bigquery') {
      const parts = d.id.split('.');
      if (parts.length === 3) {
        const [project, dataset, table] = parts;
        if (!tree.bigquery[project]) tree.bigquery[project] = {};
        if (!tree.bigquery[project][dataset]) tree.bigquery[project][dataset] = [];
        tree.bigquery[project][dataset].push({ ...d, tableName: table });
      } else if (parts.length === 2) {
        const [dataset, table] = parts;
        const project = 'Current Project';
        if (!tree.bigquery[project]) tree.bigquery[project] = {};
        if (!tree.bigquery[project][dataset]) tree.bigquery[project][dataset] = [];
        tree.bigquery[project][dataset].push({ ...d, tableName: table });
      } else {
        const fallbackProject = 'Direct Connections';
        const fallbackDataset = 'Generic';
        if (!tree.bigquery[fallbackProject]) tree.bigquery[fallbackProject] = {};
        if (!tree.bigquery[fallbackProject][fallbackDataset]) {
          tree.bigquery[fallbackProject][fallbackDataset] = [];
        }
        tree.bigquery[fallbackProject][fallbackDataset].push({ ...d, tableName: d.name || d.id });
      }
    }
  });

  tree.manteo = splitManteoDatasets(localItems);
  tree.bigquery = sortBigQueryTreeByCreatedAt(tree.bigquery);
  return tree;
};

const datasetMatchesSearch = (item, query) =>
  (item.name || item.id || item.tableName || '').toLowerCase().includes(query);

/** Filter dataset tree by search query (name / id). */
export const filterDatasetTree = (tree, searchQuery) => {
  const q = searchQuery.trim().toLowerCase();

  const manteo = emptyManteoSubgroups();
  if (!q) {
    manteo[MANTEO_USER_DATA_KEY] = tree.manteo[MANTEO_USER_DATA_KEY] || [];
    manteo[MANTEO_DATA_CATALOG_KEY] = tree.manteo[MANTEO_DATA_CATALOG_KEY] || [];
  } else {
    manteo[MANTEO_USER_DATA_KEY] = (tree.manteo[MANTEO_USER_DATA_KEY] || []).filter((item) =>
      datasetMatchesSearch(item, q)
    );
    manteo[MANTEO_DATA_CATALOG_KEY] = (tree.manteo[MANTEO_DATA_CATALOG_KEY] || []).filter((item) =>
      datasetMatchesSearch(item, q)
    );
  }

  const bigquery = Object.fromEntries(
    Object.entries(tree.bigquery || {})
      .map(([projectId, datasets]) => {
        const filteredDatasets = Object.fromEntries(
          Object.entries(datasets)
            .map(([datasetId, items]) => [
              datasetId,
              q
                ? items.filter((item) => datasetMatchesSearch(item, q))
                : items,
            ])
            .filter(([, items]) => items.length > 0)
        );
        return [projectId, filteredDatasets];
      })
      .filter(([, datasets]) => Object.keys(datasets).length > 0)
  );

  return { manteo, bigquery };
};

export const hasManteoDatasets = (manteoSubgroups) =>
  MANTEO_SUBGROUP_ORDER.some((key) => (manteoSubgroups?.[key] || []).length > 0);

export const manteoDatasetCount = (manteoSubgroups) =>
  MANTEO_SUBGROUP_ORDER.reduce((count, key) => count + (manteoSubgroups?.[key] || []).length, 0);

/** @returns {{ bigquery: Record<string, Record<string, object[]>>, custom: object[] }} */
export const buildSortedFunctionTree = (udfs) => {
  const tree = {
    bigquery: {},
    custom: [],
  };

  (Array.isArray(udfs) ? udfs : []).forEach((func) => {
    if (func.source === 'bigquery' && func.routine_id) {
      const parts = func.routine_id.split('.');
      if (parts.length >= 3) {
        const project = parts[0];
        const dataset = parts[1];
        const functionName = parts.slice(2).join('.');

        if (!tree.bigquery[project]) tree.bigquery[project] = {};
        if (!tree.bigquery[project][dataset]) tree.bigquery[project][dataset] = [];
        tree.bigquery[project][dataset].push({
          ...func,
          project,
          dataset,
          functionName,
        });
      }
    } else if (func.source === 'custom') {
      tree.custom.push(func);
    }
  });

  tree.custom = sortByCreatedAtDesc(tree.custom);
  tree.bigquery = sortBigQueryTreeByCreatedAt(tree.bigquery);
  return tree;
};

export const collectDatasetTreeNodeIds = (tree) => {
  const ids = new Set();

  if (hasManteoDatasets(tree.manteo)) {
    ids.add(MANTEO_CLOUD_ID);
    MANTEO_SUBGROUP_ORDER.forEach((subKey) => {
      if ((tree.manteo?.[subKey] || []).length > 0) {
        ids.add(manteoSubgroupNodeId(subKey));
      }
    });
  }

  if (Object.keys(tree.bigquery || {}).length > 0) ids.add('bigquery');
  Object.keys(tree.bigquery || {}).forEach((project) => {
    ids.add(project);
    Object.keys(tree.bigquery[project]).forEach((dataset) => {
      ids.add(dataset);
    });
  });
  return ids;
};

export const collectFunctionTreeNodeIds = (tree) => {
  const ids = new Set();
  if (Object.keys(tree.bigquery || {}).length > 0) ids.add('bigquery');
  if (tree.custom?.length > 0) ids.add('custom');
  Object.keys(tree.bigquery || {}).forEach((project) => {
    ids.add(project);
    Object.keys(tree.bigquery[project]).forEach((dataset) => {
      ids.add(dataset);
    });
  });
  return ids;
};

/** Dataset + function modal trees ({ manteo, bigquery, custom }). */
export const collectItemsTreeNodeIds = (tree) => {
  const ids = collectDatasetTreeNodeIds(tree);
  if (tree.custom?.length > 0) ids.add('custom');
  return ids;
};

export const InventoryTreeNode = ({
  item,
  level = 0,
  onToggle,
  onSelect,
  children,
  isExpanded,
  icon: Icon,
  iconColor = 'text-gray-400',
  isSelected,
  isDisabled,
  isAlreadyAdded,
  count,
  useCheckbox = false,
}) => {
  const isLeaf = !children;
  const indent = 8 + level * 14;

  const renderGroupIcon = () => {
    if (Icon === Database) {
      return <Database size={13} strokeWidth={2.5} className={iconColor} />;
    }
    if (Icon === Server) {
      return isExpanded
        ? <Server size={13} className={iconColor} />
        : <Server size={13} className={iconColor} />;
    }
    if (Icon === Folder) {
      return isExpanded
        ? <FolderOpen size={12} className={iconColor} />
        : <Folder size={12} className={iconColor} />;
    }
    return isExpanded
      ? <FolderOpen size={13} className={iconColor} />
      : <Folder size={13} className={iconColor} />;
  };

  return (
    <div className="select-none">
      <div
        role={isLeaf ? 'option' : 'button'}
        aria-expanded={!isLeaf ? isExpanded : undefined}
        className={cn(
          'flex items-center gap-2 transition-colors',
          isLeaf ? 'rounded-md py-1.5 pr-2' : 'py-1 pr-2',
          isLeaf && !isDisabled && 'cursor-pointer hover:bg-gray-100/80 dark:hover:bg-gray-800/60',
          isLeaf && isSelected && !useCheckbox && 'rounded-md bg-emerald-50 dark:bg-emerald-950/40 ring-1 ring-inset ring-emerald-500/25',
          isLeaf && isSelected && useCheckbox && 'rounded-md bg-emerald-50/60 dark:bg-emerald-950/25',
          isDisabled && 'opacity-45 cursor-not-allowed'
        )}
        style={{ paddingLeft: indent }}
        onClick={() => {
          if (isDisabled) return;
          if (isLeaf) {
            onSelect?.(item);
          } else {
            onToggle?.();
          }
        }}
      >
        <div className="flex w-3.5 shrink-0 items-center justify-center">
          {!isLeaf && (
            isExpanded
              ? <ChevronDown size={level === 0 ? 12 : 11} className="text-gray-400" />
              : <ChevronRight size={level === 0 ? 12 : 11} className="text-gray-400" />
          )}
        </div>

        {isLeaf && useCheckbox ? (
          <input
            type="checkbox"
            checked={!!isSelected}
            disabled={isDisabled}
            onChange={() => onSelect?.(item)}
            onClick={(e) => e.stopPropagation()}
            className="h-3.5 w-3.5 shrink-0 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500/40 disabled:cursor-not-allowed dark:border-gray-600 dark:bg-gray-900"
            aria-label={`Include ${item.name || item.id}`}
          />
        ) : (
          <div
            className={cn(
              'flex shrink-0 items-center justify-center',
              isLeaf ? 'h-5 w-5 rounded' : '',
              isLeaf && isSelected && 'bg-emerald-600 text-white',
              isLeaf && !isSelected && 'text-gray-400'
            )}
          >
            {isLeaf ? (
              isSelected ? (
                <Check size={12} strokeWidth={2.5} />
              ) : (
                Icon ? <Icon size={12} /> : <Box size={12} />
              )
            ) : (
              renderGroupIcon()
            )}
          </div>
        )}

        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 items-center gap-2">
            <span
              className={cn(
                'truncate',
                isLeaf
                  ? 'text-[11px] font-bold tracking-tight text-gray-900 dark:text-gray-100'
                  : cn(
                    'font-black uppercase tracking-wider',
                    level === 0 ? 'text-[10px] text-gray-700 dark:text-gray-300' : 'text-[9px] text-gray-600 dark:text-gray-400'
                  )
              )}
            >
              {item.name || item.id}
            </span>
            {!isLeaf && count != null && (
              <span className="text-[8px] font-black uppercase tracking-widest text-gray-400 dark:text-gray-500">
                ({count})
              </span>
            )}
            {isLeaf && item.status && item.status !== 'ready' && (
              <span
                className={cn(
                  'shrink-0 rounded px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest',
                  item.status === 'failed'
                    ? 'bg-red-100 text-red-600 dark:bg-red-950/50 dark:text-red-400'
                    : 'bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-400'
                )}
              >
                {item.status === 'loading' ? 'Loading' : item.status === 'generating' ? 'Indexing' : item.status}
              </span>
            )}
          </div>
          {item.signature && (
            <p className="truncate text-[11px] font-mono text-gray-400 dark:text-gray-500">{item.signature}</p>
          )}
        </div>

        {isLeaf && (
          <div className="shrink-0 text-right">
            {isAlreadyAdded ? (
              <span className="text-[10px] font-black uppercase tracking-widest text-gray-400">Linked</span>
            ) : item.created_at ? (
              <span className="text-[10px] tabular-nums text-gray-400">{formatItemDate(item.created_at)}</span>
            ) : item.type ? (
              <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400">{item.type}</span>
            ) : null}
          </div>
        )}
      </div>

      {isExpanded && children}
    </div>
  );
};

export const ManteoCloudTreeSection = ({
  manteoSubgroups,
  userFolderLabel = 'My data',
  expandedNodes,
  onToggleNode,
  renderDatasetLeaf,
}) => {
  const subgroups = MANTEO_SUBGROUP_ORDER.map((key) => ({
    key,
    label: key === MANTEO_DATA_CATALOG_KEY ? 'data-catalog' : userFolderLabel,
    items: manteoSubgroups?.[key] || [],
  })).filter((group) => group.items.length > 0);

  if (subgroups.length === 0) return null;

  return (
    <InventoryTreeNode
      item={{ id: MANTEO_CLOUD_ID, name: 'Manteo cloud' }}
      level={0}
      isExpanded={expandedNodes.has(MANTEO_CLOUD_ID)}
      onToggle={() => onToggleNode(MANTEO_CLOUD_ID)}
      icon={Database}
      iconColor="text-emerald-500"
      count={manteoDatasetCount(manteoSubgroups)}
      children={
        <div>
          {subgroups.map(({ key, label, items }) => (
            <InventoryTreeNode
              key={key}
              item={{ id: manteoSubgroupNodeId(key), name: label }}
              level={1}
              isExpanded={expandedNodes.has(manteoSubgroupNodeId(key))}
              onToggle={() => onToggleNode(manteoSubgroupNodeId(key))}
              icon={Folder}
              iconColor="text-emerald-500"
              count={items.length}
              children={
                <div>
                  {items.map((item) => renderDatasetLeaf(item, 2))}
                </div>
              }
            />
          ))}
        </div>
      }
    />
  );
};

export const BigQueryDatasetTreeSection = ({
  bigqueryTree,
  expandedNodes,
  onToggleNode,
  renderDatasetLeaf,
}) => {
  if (!bigqueryTree || Object.keys(bigqueryTree).length === 0) return null;

  const tableCount = Object.values(bigqueryTree).reduce(
    (count, datasets) =>
      count + Object.values(datasets).reduce((inner, items) => inner + items.length, 0),
    0
  );

  return (
    <InventoryTreeNode
      item={{ id: 'bigquery', name: 'BigQuery' }}
      level={0}
      isExpanded={expandedNodes.has('bigquery')}
      onToggle={() => onToggleNode('bigquery')}
      icon={Server}
      iconColor="text-blue-500"
      count={tableCount}
      children={
        <div>
          {Object.entries(bigqueryTree).map(([projectId, datasets]) => (
            <InventoryTreeNode
              key={projectId}
              item={{ id: projectId, name: projectId }}
              level={1}
              isExpanded={expandedNodes.has(projectId)}
              onToggle={() => onToggleNode(projectId)}
              icon={Folder}
              iconColor="text-blue-500"
              count={Object.values(datasets).reduce((n, items) => n + items.length, 0)}
              children={
                <div>
                  {Object.entries(datasets).map(([datasetId, items]) => (
                    <InventoryTreeNode
                      key={datasetId}
                      item={{ id: datasetId, name: datasetId }}
                      level={2}
                      isExpanded={expandedNodes.has(datasetId)}
                      onToggle={() => onToggleNode(datasetId)}
                      icon={Folder}
                      iconColor="text-blue-400"
                      count={items.length}
                      children={
                        <div>
                          {items.map((item) => renderDatasetLeaf(item, 3))}
                        </div>
                      }
                    />
                  ))}
                </div>
              }
            />
          ))}
        </div>
      }
    />
  );
};

export const DatasetInventoryTree = ({
  tree,
  userFolderLabel = 'My data',
  expandedNodes,
  onToggleNode,
  renderDatasetLeaf,
}) => (
  <>
    <ManteoCloudTreeSection
      manteoSubgroups={tree.manteo}
      userFolderLabel={userFolderLabel}
      expandedNodes={expandedNodes}
      onToggleNode={onToggleNode}
      renderDatasetLeaf={renderDatasetLeaf}
    />
    <BigQueryDatasetTreeSection
      bigqueryTree={tree.bigquery}
      expandedNodes={expandedNodes}
      onToggleNode={onToggleNode}
      renderDatasetLeaf={renderDatasetLeaf}
    />
  </>
);

export const datasetLeafItem = (item) => ({
  id: item.id,
  name: item.tableName || item.name || item.id,
  type: item.type,
  status: item.status,
  created_at: item.created_at,
});

export const datasetLeafIcon = Table;
